// src/features/capacity/capacityUrl.js
//
// URL contract for /places/capacity. Everything shareable lives in the query
// string; neutral/default values are omitted so the canonical URL is short.
// Retired Pressure/Scenario parameters (horizon, basis, layer, whatif, tab)
// are dropped deliberately; legacy expandMarket becomes `open`.

import { LEVERS, NEUTRAL_LEVERS } from "./capacityModel.js";

export const DEFAULT_SORT = "people";
export const DEFAULT_SORT_DIR = "desc";
export const capacityDefaultDirection = key => key === DEFAULT_SORT ? DEFAULT_SORT_DIR : "asc";
export const SORT_KEYS = ["issue", "market", "people", "seats", "pressure"];
export const STATUS_FILTERS = ["over", "tight", "comfortable", "under", "no_data", "partial", "history", "no_seats", "excluded"];
const LEVER_PARAM = { growth: "g", days: "days", sharing: "share", hold: "hold", conf: "conf", waste: "waste" };

function validLever(lever, raw) {
  if (raw == null || raw === "") return null;
  const n = Number(raw);
  if (!Number.isFinite(n) || n < lever.min - 1e-9 || n > lever.max + 1e-9) return null;
  const steps = (n - lever.min) / lever.step;
  if (Math.abs(steps - Math.round(steps)) > 1e-6) return null;
  return Math.round(n * 10) / 10;
}
const list = (raw) => (raw ? raw.split(",").map((s) => s.trim()).filter(Boolean) : []);

export function parseCapacityState(search = "") {
  const p = new URLSearchParams(search);
  const levers = { ...NEUTRAL_LEVERS };
  for (const l of LEVERS) {
    const v = validLever(l, p.get(LEVER_PARAM[l.key]));
    if (v != null) levers[l.key] = v;
  }
  const focus = /^\d{4}-(0[1-9]|1[0-2])$/.test(p.get("focus") || "") ? p.get("focus") : null;
  const sort = SORT_KEYS.includes(p.get("sort")) ? p.get("sort") : DEFAULT_SORT;
  const open = [...new Set([...list(p.get("open")), ...list(p.get("expandMarket"))])];
  return {
    view: p.get("view") === "quarterly" ? "quarterly" : "monthly",
    focus,
    panel: p.get("panel") === "scenario" ? true : p.get("panel") === "closed" ? false : null,
    levers,
    q: (p.get("q") || "").slice(0, 120),
    region: list(p.get("region")),
    status: [...new Set(list(p.get("status")).map(s => s === "densify" ? "over" : s).filter((s) => STATUS_FILTERS.includes(s)))],
    // Legacy empty params remain unrestricted. Only new checked links can
    // express none; omitted groups stay dynamic/unrestricted in either format.
    regionNone: p.get("filters") === "checked" && p.has("region") && p.get("region") === "",
    statusNone: p.get("filters") === "checked" && p.has("status") && p.get("status") === "",
    sort,
    dir: ["asc", "desc"].includes(p.get("dir")) ? p.get("dir") : capacityDefaultDirection(sort),
    open,
  };
}

export function serializeCapacityState(state) {
  const p = new URLSearchParams();
  if (state.view === "quarterly") p.set("view", "quarterly");
  if (state.focus) p.set("focus", state.focus);
  if (state.panel === true) p.set("panel", "scenario");
  if (state.panel === false) p.set("panel", "closed");
  for (const l of LEVERS) {
    const v = state.levers?.[l.key];
    if (v != null && Math.abs(v - l.neutral) > 1e-9) p.set(LEVER_PARAM[l.key], String(v));
  }
  if (state.q) p.set("q", state.q);
  if (state.regionNone || state.statusNone) p.set("filters", "checked");
  if (state.regionNone) p.set("region", "");
  else if (state.region?.length) p.set("region", [...new Set(state.region)].join(","));
  if (state.statusNone) p.set("status", "");
  else if (state.status?.length) p.set("status", [...new Set(state.status)].join(","));
  if (state.sort && state.sort !== DEFAULT_SORT) p.set("sort", state.sort);
  if (state.dir && state.dir !== capacityDefaultDirection(state.sort || DEFAULT_SORT)) p.set("dir", state.dir);
  if (state.open?.length) p.set("open", state.open.join(","));
  const s = p.toString();
  return s ? `?${s}` : "";
}

// Old /places/pressure and /places/scenario links: old Scenario links always
// meant "panel open"; everything else round-trips through the parser.
export function normalizeCapacitySearch(search = "", { fromLens = null } = {}) {
  const state = parseCapacityState(search);
  if (fromLens === "scenario") state.panel = true;
  return serializeCapacityState(state);
}
